import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, ChangeDetectorRef, OnChanges } from '@angular/core';
import { trigger, state, style, transition, animate } from '@angular/animations';

import { DialogService } from 'primeng/dynamicdialog';

import { HelperService, HTTPService, DateService, DataService, FormatService } from 'app/services';
import { defaultWorkFlows, config, time as timeOptions } from 'app/constants';
import { datetimeFormat } from 'app/constants/date-time-format';
import { ConfirmDismissDialogComponent } from 'app/modules/shared/components';
import { MilageInformationDialogComponent } from 'app/modules/shared/components/milage-information-dialog/milage-information-dialog.component';

import { debounce, find, isEqual, sortBy } from 'lodash';
import { UpdateInvoiceDateDialogComponent } from '../update-invoice-date-dialog/update-invoice-date-dialog.component';

@Component({
  selector: 'app-estimate-detail',
  templateUrl: './estimate-detail.component.html',
  styleUrls: ['./estimate-detail.component.scss'],
  animations: [
    trigger('toggleBox', [
      state(
        'open',
        style({
          height: '100%',
        })
      ),
      state(
        'closed',
        style({
          height: '0px',
        })
      ),
      transition('open => closed', [animate('.3s')]),
      transition('closed => open', [animate('0.3s')]),
    ]),
  ],
})
export class EstimateDetailComponent implements OnInit, OnChanges, OnDestroy {
  @Input() carfaxReportSent: boolean = false;
  @Input() carfaxUrgentReportingEnabled: boolean = false;
  @Input() isCarfaxUrgentPostingEnabled: boolean = false;
  @Input() upcomingFollowUps: any[] = [];
  @Input() recommendedFollowUps: any[] = [];
  @Input() communications: any[] = [];
  @Input() technicians: any[] = [];
  @Input() workFlowStatuses: any = [];
  @Input() isCarfaxEnabled: boolean = false;
  @Input() enableMandatoryVin: boolean = false;
  @Input() isManualInvCodeEnabled: boolean = false;
  @Input() isArchived: boolean = false;
  @Input() isServiceWriterUpdating: boolean = false;
  @Input() serviceWriterEnabled: boolean = false;
  @Input() carfaxReportingEnabled: boolean = false;
  @Input() isDeferredServiceEnabled: boolean = false;
  @Input() canUpdateInvoiceNumber: boolean = false;
  @Input() canRevertToEstimate: boolean = false;
  @Input() isOrderStatusUpdating: boolean = false;
  @Input() isInvCodEqToEstCode: boolean = false;
  @Input() isSubletTrackingEnabled: boolean = false;
  @Input() createdBy = {};
  @Input() authServiceStatusCount = {
    pending: 0,
    approved: 0,
    deferred: 0,
    declined: 0,
    archived: 0,
    deferredLocked: 0,
  };
  @Input() kanbanSettings: any = {
    invoice: {},
    paid: {},
  };
  @Input('invoiceDate')
  set invoiceDate(invoiceDate: any) {
    if (invoiceDate) {
      this._estimate.invoiceDate = invoiceDate;
      invoiceDate = this.dateService.getCurrentDateTimeTz(new Date(invoiceDate).getTime());
      this.invoiceDateTime.date = new Date(invoiceDate);
      this.invoiceDateTime.time = new Date(invoiceDate);

      this.setInvoicedBy();
    }
  }
  @Input('estimate')
  set estimate(data: any) {    
    if (data) {
      this._estimate = data;
      this.oldEstimate = this.helperService.deepCopy(data);

      // for intialization of view
      this.getFiles(this._estimate._id);

      // Set order authorization
      if (this._estimate.authorized) {
        this.authorized = this.authorization[1];
        this.setAuthorizedBy();
      }

      if (this._estimate.orderStatus === 'invoice') {
        this.setInvoicedBy();
      }

      // Set order status
      const [status] = this.orderStatus.filter(({ value }) => value === this._estimate.orderStatus);

      if (status) {
        this.status = status;
      }

      this.setServiceWriter(this._estimate.serviceWriter || {});
      // Set workflow status
      this.workFlowStatus = this._estimate.workFlowStatus || {};
      this.updatePaymentStatus(this._estimate);
      this.authorizedByCustomer = data.authByCustomer
    }
  }
  @Input('refetchDocuments')
  set refetchDocuments(refetchDocuments: boolean) {
    if (this.isInitialDocFetchDone) {
      this.getFiles(this._estimate._id);
    }
  }
  @Input('isMarketingEnabled')
  set isMarketingEnabled(isMarketingEnabled: boolean) {
    this._isMarketingEnabled = isMarketingEnabled;
    if (isMarketingEnabled) {
      this.getFollowUps();
    }
  }
  @Input('campignSuggestions')
  set campignSuggestions(campaign: any) {
    this.sourceCampaigns = {
      followUps: [],
      promotionalCampaigns: [],
      leadCampaigns: [],
    };

    if (campaign.followUps && campaign.followUps.length) {
      campaign.followUps.forEach((followup: any) => {
        followup.sendDate = this.dateService.getCurrentDateTimeTz(new Date(followup.sendDate).getTime()).getTime();
        this.sourceCampaigns.followUps.push(followup);
      });
    }
    if (campaign.promotionalCampaigns && campaign.promotionalCampaigns.length) {
      campaign.promotionalCampaigns.forEach((prom: any) => {
        prom.sendDate = this.dateService.getCurrentDateTimeTz(new Date(prom.sendDate).getTime()).getTime();
        this.sourceCampaigns.promotionalCampaigns.push(prom);
      });
    }
    if (campaign.leadCampaigns && campaign.leadCampaigns.length) this.sourceCampaigns.leadCampaigns = campaign.leadCampaigns;
    this._campignSuggestions = [...this.sourceCampaigns.followUps, ...this.sourceCampaigns.promotionalCampaigns, ...this.sourceCampaigns.leadCampaigns];
  }
  @Input() isFinalizedRo: boolean = false;

  @Output() statusChange = new EventEmitter();
  @Output() sendInvoice = new EventEmitter();
  @Output() setPageTitle = new EventEmitter();
  @Output() updateInvoiceDateTime = new EventEmitter();
  @Output() updateOrderStatusProcessFlag = new EventEmitter();
  @Output() updateOrderService = new EventEmitter();
  @Output() appOpenAuthDialog = new EventEmitter();
  @Output() updateInvoiceCode = new EventEmitter();
  @Output() updateSourceCampaign = new EventEmitter();
  @Output() openVehicleInSidepanel = new EventEmitter();
  @Output() checkPendingServices = new EventEmitter();

  orderWorkflowUpdate: any;
  showToaster: string = 'hide';
  toaster: any;
  isOpen: boolean = false;
  isInvoiceCodeUpdating: boolean = false;
  _isMarketingEnabled: boolean = false;
  authorization: any = config.estimate.authorization;
  orderStatus: any = config.estimate.orderStatus;

  authorized: any = this.authorization[0];
  status: any = this.orderStatus[0];
  workFlowStatus: any = {};
  _estimate: any = {};
  oldEstimate: any;
  pdfs: any = {
    estimate: null,
    invoice: null,
    inspection: null,
  };
  authorizedBy: any = {
    name: '',
    datetime: '',
  };
  invoiceBy: any = {
    name: '',
    datetime: '',
  };
  interval: any;
  timeOptions: any[] = this.helperService.deepCopy(timeOptions);
  maxDateRange = new Date();
  authDate = new Date();
  invoiceDateTime: any = {
    date: '',
    time: '',
  };
  isInitialDocFetchDone: boolean = false;
  newInvCode: string = '';
  _handleCheckForUniqueInvCode = debounce(this.handleCheckForUniqueInvCode, 300);
  errors: any = {};
  sourceCampaigns: any = { followUps: [], promotionalCampaigns: [] };
  sourceCampaign: any = {};
  _campignSuggestions: any = [];
  paymentStatusOptions: any = [
    { id: 'fullyPaid', name: 'Paid', isChecked: false, value: 'isPaidFull' },
    { id: 'unpaid', name: 'Unpaid', isChecked: false, value: 'isUnpaid' },
    { id: 'partialPaid', name: 'Partially paid', isChecked: false, value: 'isPaidPartial' },
  ];
  dataServiceListeners: any[] = [];
  authorizedByCustomer: boolean = false;

  constructor(
    private dataService: DataService,
    public helperService: HelperService,
    private dialogService: DialogService,
    private httpService: HTTPService,
    public dateService: DateService,
    private cdr: ChangeDetectorRef,
    private formatService: FormatService
  ) { }

  ngOnInit() {
    this.setDataListeners();
  }

  ngOnChanges(change: any) {
    const { technicians = [] } = change;

    if (technicians.currentValue) {
      if (!technicians.previousValue !== technicians.currentValue) {
        this.setServiceWriter(this._estimate.serviceWriter);
      }
    }

  }

  updatePaymentStatus(order) {
    this._estimate.paymentStatus = order.isPaidFull ? this.paymentStatusOptions[0] : order.isPaidPartial ? this.paymentStatusOptions[2] : this.paymentStatusOptions[1];
    if (!this._estimate.isDateUpdated) {
      this._estimate.paymentStatusUpdateDate = order.paymentStatusUpdateDate ?
        this.dateService.formatDate(this.dateService.getCurrentDateTimeTz(new Date(order.paymentStatusUpdateDate).getTime()), datetimeFormat.mmmdyyhhmmaa) : '';
    }
    this._estimate.isDateUpdated = true;
  }

  setDataListeners() {
    const listener = this.dataService.getData().subscribe((obj) => {
      const data = obj.data;

      if (data.key === 'update-payment-status' && data.data && data.data.order) {
        this._estimate.isDateUpdated = false;
        this.updatePaymentStatus(data.data.order);
      }
    });
    this.dataServiceListeners.push(listener);
  }

  ngOnDestroy() {
    this.dataServiceListeners.forEach((listener) => {
      try {
        listener.unsubscribe();
      } catch (err) { }
    });

    if (this.orderWorkflowUpdate) {
      this.orderWorkflowUpdate.unsubscribe();
    }

    if (this.interval) {
      clearTimeout(this.interval);
    }
  }

  getSourceCampaign() {
    const { customer = {}, vehicle = {}, _id } = this._estimate;

    if (!customer.customerId || !vehicle.vehicleId) {
      return;
    }

    const data = {
      createdAt: this.dateService.formatDate(this._estimate.createdAt, datetimeFormat.yyymmddhhmm),
      customerId: (customer.customerId && customer.customerId._id) || customer.customerId,
      vehicleId: (vehicle.vehicleId && vehicle.vehicleId._id) || vehicle.vehicleId,
    };
    this.httpService.getSourceCampaigns(data).then(
      (campaign: any) => {
        this.campignSuggestions = [];

        this.sourceCampaigns = {
          followUps: [],
          promotionalCampaigns: [],
        };

        if (campaign && campaign.followUps.length) {
          campaign.followUps.forEach((followup: any) => {
            followup.sendDate = this.dateService.getCurrentDateTimeTz(new Date(followup.sendDate).getTime()).getTime();
            this.sourceCampaigns.followUps.push(followup);
          });
        }
        if (campaign && campaign.promotionalCampaigns.length) {
          campaign.promotionalCampaigns.forEach((prom: any) => {
            prom.sendDate = this.dateService.getCurrentDateTimeTz(new Date(prom.sendDate).getTime()).getTime();
            this.sourceCampaigns.promotionalCampaigns.push(prom);
          });
        }
        this.campignSuggestions = [...this.sourceCampaigns.followUps, ...this.sourceCampaigns.promotionalCampaigns];
      },
      (err) => {
        this.helperService.showToaster('error', err);
      }
    );
  }

  getFollowUps = debounce(() => {
    const { customer = {}, vehicle = {}, _id } = this._estimate;

    if (!customer || !vehicle) return;

    const customerId = (customer.customerId && customer.customerId._id) || customer.customerId;
    const vehicleId = (vehicle.vehicleId && vehicle.vehicleId._id) || vehicle.vehicleId;

    if (!customerId || !vehicleId) return;

    this.httpService.getScheduledFollowUp(customerId, { vehicleId, sortField: 'scheduleAt', sortOrder: -1 }).then(
      (followUps) => {
        const { upcomingFollowUps = [] } = this.formatService.formatScheduledFollowUp(followUps, customer);

        this.upcomingFollowUps = upcomingFollowUps;
      },
      (err) => {
        this.helperService.showToaster('error', err);
      }
    );
  }, 2000);

  setServiceWriter(serviceWriter: any = {}) {
    if (!serviceWriter.serviceWriterId && this.technicians.length) {
      const serviceWriter = find(this.technicians, {
        _id: this._estimate.createdBy,
      });

      if (serviceWriter) {
        serviceWriter.serviceWriterId = serviceWriter._id;
        this._estimate.serviceWriter = serviceWriter;
      }
    }
  }

  async getFiles(orderId: string) {
    try {
      const files: any = await this.httpService.getDocument(orderId);
      if (files && files.length && files.length > 0) {
        const attachments = files.map(({ entityType, link, createdAt }) => ({
          type: entityType,
          url: link,
          date: createdAt,
        }));
        this.fetchLatestPDFs(attachments);
      } else {
        // backward compatibilty for docs that are not saved in docs collections
        this.fetchLatestPDFs(this._estimate.attachments);
      }
    } catch (err) {
      this.helperService.showToaster('error', err);
    } finally {
      this.isInitialDocFetchDone = true;
    }
  }

  setAuthorizedBy() {
    this.authorizedBy = {
      name: this.renderName(this._estimate.authorizedBy),
      datetime: this.dateService.getCurrentDateTimeTz(new Date(this._estimate.approvedAt).getTime()),
    };
    this.authorizedBy.date = this.dateService.formatDate(this.authorizedBy.datetime, datetimeFormat.mmmdyyyy);
    this.authorizedBy.time = this.dateService.formatDate(this.authorizedBy.datetime, datetimeFormat.hhmma);
  }

  setInvoicedBy() {
    this.invoiceBy = {
      name: this.renderName(this._estimate.invoiceBy),
      datetime: this.dateService.getCurrentDateTimeTz(new Date(this._estimate.invoiceDate).getTime()),
    };
    this.invoiceBy.date = this.dateService.formatDate(this.invoiceBy.datetime, datetimeFormat.mmmdyyyy);
    this.invoiceBy.time = this.dateService.formatDate(this.invoiceBy.datetime, datetimeFormat.hhmma);
  }

  handleCarfaxUrgentReporting(value: boolean) {
    this.carfaxUrgentReportingEnabled = value;
    if (value) {
      const ref = this.dialogService.open(ConfirmDismissDialogComponent, {
        width: '30%',
        styleClass: 'small-heading-dialog carfax-customized-confirmation',
        showHeader: false,
        data: {
          isCustomizedConfirmationBox: true,
          isHTML: true,
          styleClassHeading: 'font-18 font-700',
          styleClassCloseIcon: 'font-24 font-400 font-gray',
          title: `Important Notice`,
          headerIcon: 'fa fa-exclamation-triangle font-icon-red',
          message: `
          <span>
            <p>By enabling this option, your Repair Order (RO) will be reported to Carfax one day after invoicing and submitted <strong>only once.</strong></p>
            <p>Please ensure the RO details are completed fully at the time of Invoicing. Any updates to RO details after submission to Carfax will not be re-sent to Carfax. </p>
            <p>Are you sure you want to proceed?</p>
          </span>`,
          button: {
            success: {
              title: 'Proceed',
              styleClass: 'btn-danger',
            },
            cancel: { title: 'Cancel' },
          },
        },
      });

      const onClose = ref.onClose.subscribe(({ isSuccess }) => {
        if (isSuccess) {
          this.carfaxUrgentReportingEnabled = true;
          this.updateCarfaxReportStatus();
        } else {
          this.carfaxUrgentReportingEnabled = false;
          this.cdr.detectChanges();
        }
        onClose.unsubscribe();
      });
    } else {
      this.updateCarfaxReportStatus();
    }
  }

  handleCarfaxReportingUpdate(carfaxReportingEnabled: boolean) {
    this.carfaxReportingEnabled = carfaxReportingEnabled;

    if (!carfaxReportingEnabled) {
      const ref = this.dialogService.open(ConfirmDismissDialogComponent, {
        width: '30%',
        styleClass: 'small-heading-dialog',
        showHeader: false,
        data: {
          styleClassHeading: 'font-14',
          title: 'Are you sure you want to opt out of Carfax reporting?',
          message: 'If you confirm, this RO will not be reported to Carfax',
          button: {
            success: { title: 'Confirm' },
            cancel: { title: 'Cancel' },
          },
        },
      });

      const onClose = ref.onClose.subscribe(({ isSuccess, data }) => {
        if (isSuccess) {
          this.carfaxUrgentReportingEnabled = carfaxReportingEnabled;
          this.updateCarfaxReportStatus();
        } else {
          this.carfaxReportingEnabled = true;
          this.cdr.detectChanges();
        }

        onClose.unsubscribe();
      });
    } else {
      this.updateCarfaxReportStatus();
    }
  }

  updateCarfaxReportStatus() {
    const payload: any = {
      carfaxReportingEnabled: this.carfaxReportingEnabled,
    };
    if (this.isCarfaxUrgentPostingEnabled) {
      payload.carfaxUrgentReportingEnabled = this.carfaxUrgentReportingEnabled && this.carfaxReportingEnabled;
    }
    this.httpService.updateOrderDetails(this._estimate._id, payload).then(
      (res) => {
        this.helperService.showToaster('success', 'Successfully updated');
      },
      (err) => {
        this.helperService.showToaster('error', err);
      }
    );
  }

  handleSourceCampaignChange(campaign: any) {
    this.updateSourceCampaign.emit({
      ...campaign,
      campaignId: campaign._id,
    });
  }

  onStatusSelect(type: string, data: any) {
    const { customer = {} } = this._estimate || {};

    if (!customer.customerId) {
      this.helperService.showToaster('error', 'Please select customer first!');
      return;
    }

    switch (type) {
      case 'authorization':
        if (this.oldEstimate.authorized !== data.authorized) {
          this.openConfirmationModal(data, type);
        }
        break;

      case 'workFlowStatus':
        if (this.workFlowStatus.statusId !== data._id) {

          let isOpenSubletModal = false;
          if (this.isSubletTrackingEnabled &&
            data.name === "Invoice"
          ) {
            this.httpService.getSubletItems(this._estimate._id)
              .then(
                (res: any) => {
                  if(res?.length) {
                    res.forEach(sublet => {
                      const subletItem = sublet.items.find(item => item.itemType === 'sublet');
                      if (subletItem &&
                        (!subletItem.supplier || !subletItem.supplier.name) ||
                        (subletItem.workStatus == 'inProgress') ||
                        (subletItem.workStatus == 'notStarted')
                      ) {
                        isOpenSubletModal = true;
                      }
                      if (isOpenSubletModal) {
                        let payload = {
                          type: 'orderStatus',
                          data: {
                            name: "INVOICE",
                            send: "Send Invoice",
                            value: "invoice"
                          }
                        }
                        this.onStatusSelect(payload.type, payload.data);
                      } else {
                        this.openConfirmationModal(data, type);
                      }
                    });
                  } else {
                    this.openConfirmationModal(data, type);
                  }
                });
          } else {
            this.openConfirmationModal(data, type);
          }
        }
        break;

      case 'orderStatus':
        if (this.isOrderStatusUpdating) {
          return;
        }
        this.isOrderStatusUpdating = false;
        this.updateOrderStatusProcessFlag.emit(true);
        if (this.oldEstimate.orderStatus !== data.value) {
          this.statusChange.emit({
            type,
            orderStatus: data.value,
            estimate: { ...this._estimate, orderStatus: data.value },
          });
        }
        break;
    }
  }

  onSelectServiceWriter(type: string, data: any) {
    if (this.isServiceWriterUpdating) {
      return;
    }

    const serviceWriter = {
      serviceWriterId: data._id,
      name: data.name,
    };

    if (isEqual(this._estimate.serviceWriter, serviceWriter)) {
      return;
    }

    this.isServiceWriterUpdating = true;
    const ref = this.dialogService.open(ConfirmDismissDialogComponent, {
      width: '30%',
      styleClass: 'small-heading-dialog',
      showHeader: false,
      data: {
        styleClassHeading: 'font-14',
        title: 'Change service advisor',
        message: `Are you sure you want to change ${this._estimate.serviceWriter && this._estimate.serviceWriter.name ? this._estimate.serviceWriter.name : 'service advisor'} to ${serviceWriter.name
          }?`,
        button: {
          success: { title: 'Change' },
          cancel: { title: 'Cancel' },
        },
      },
    });

    const onClose = ref.onClose.subscribe(({ isSuccess, data }) => {
      if (isSuccess) {
        this.updateServieWriter(serviceWriter);
      } else {
        this.isServiceWriterUpdating = false;
        this.cdr.detectChanges();
      }

      onClose.unsubscribe();
    });
  }

  onSearchSelect(event: any, close: any = null) {
    let payloadSourceCampaign = {
      ...event,
    };
    if (event.parentType === 'followUp') {
      payloadSourceCampaign['campaignId'] = event._id;
    } else if (event.parentType === 'marketingCampaign') {
      payloadSourceCampaign['marketingCampaignId'] = event._id;
    } else {
      payloadSourceCampaign = {
        leadCampaignId: event._id,
        title: event.title,
        category: event.category,
      };
    }
    this.updateSourceCampaign.emit(payloadSourceCampaign);

    if (close) close();
  }

  highlighedItemType(text: string, query: string) {
    // return this.helperService.highlighedItemType(text, query);
  }

  search(query: string) { }

  onSearchClear() { }

  updateServieWriter(serviceWriter: any) {
    this.httpService.updateOrderDetails(this._estimate._id, { serviceWriter }).then(
      (res) => {
        this._estimate.serviceWriter = serviceWriter;
        this.isServiceWriterUpdating = false;
        this.dataService.sendData(this.dataService.typePassData('update-service-advisor',
          { id: serviceWriter.serviceWriterId }));
        this.helperService.showToaster('success', 'Successfully updated');
      },
      (err) => {
        this.isServiceWriterUpdating = false;
        this.helperService.showToaster('error', err);
      }
    );
  }

  onSelectInvoiceDateTime(type: string, data: any) {
    this.invoiceDateTime[type] = data;
  }

  openConfirmationModal(event: any, type: string, options: { isCheckForMandatoryField: boolean } = { isCheckForMandatoryField: true }) {
    // if (options.isCheckForMandatoryField && event.name === 'Invoice') {
    //   const vehicle = this._estimate.vehicle || {};
    //   const vehicleMileage = this._estimate.vehicleMileage || {};
    //   const vehicleMileageOut = this._estimate.vehicleMileageOut || {};

    //   const requiredField = this.helperService.isMileageVinRequired({
    //     enableMandatoryVin: this.enableMandatoryVin,
    //     isMileageMandatory: this.dataService.isMileageMandatory,
    //     VIN: vehicle.VIN,
    //     mileage: vehicleMileage.reading,
    //     isTrackMileageInAndOut: this.dataService.isTrackMileageInAndOut,
    //     mileageOut: vehicleMileageOut.reading,
    //   });

    //   if (vehicle?.vehicleId && (requiredField.isMileageRequired || requiredField.isVinRequired || requiredField.isMileageOutRequired)) {
    //     // this.handleConfirm()
    //     //here we open merge prompt for enter mandatory vin or mileage and commented above code
    //     // this.statusChange.emit({
    //     //   type,
    //     //   orderStatus: event.value,
    //     //   estimate: { ...this._estimate, orderStatus: event.value },
    //     // });
    //     return;
    //   }
    // }

    let message = '';
    let title = 'Confirmation';

    if (type === 'authorization') {
      title = `Approve RO`;
      message = `This will manually update RO's authorization status`;
    } else if (type === 'workFlowStatus') {
      message = `Update WorkFlow status?`;
    }

    const ref = this.dialogService.open(ConfirmDismissDialogComponent, {
      width: '30%',
      styleClass: 'small-heading-dialog',
      showHeader: false,
      data: {
        data: { type, value: event },
        styleClassHeading: 'font-14',
        title,
        message,
        button: {
          success: { title: 'Continue' },
          cancel: { title: 'Cancel' },
        },
      },
    });

    const onClose = ref.onClose.subscribe(({ isSuccess, data }) => {
      const modalData = data.data;

      if (isSuccess) {
        this.handleConfirm(modalData.type, modalData, event);
      }

      onClose.unsubscribe();
    });
  }

  handleConfirm(type: string, data: any, event: any) {
    switch (type) {
      case 'authorization':
        this._estimate.authorized = data.value.authorized;
        this._estimate.authChanged = true;
        this.statusChange.emit({
          type,
          authorized: this._estimate.authorized,
          estimate: this._estimate,
        });
        break;

      case 'workFlowStatus':
        const { convertToInvoice } = this.kanbanSettings.invoice;
        const { paymentOnDrop } = this.kanbanSettings.invoice;
        let isOpenPaymentModal = false;

        this._estimate.wFlowChanged = true;
        this.workFlowStatus = event;
        this._estimate.workFlowStatus = {
          name: event.name,
          statusId: event._id,
        };

        // Convert order status to invoice and set the open payment modal flag
        if (convertToInvoice && (event.name === defaultWorkFlows.invoice || event.name === defaultWorkFlows.paid)) {
          if (this._estimate.orderStatus !== 'invoice') {
            if (event.name === defaultWorkFlows.paid) {
              isOpenPaymentModal = !this._estimate.isPaidFull && paymentOnDrop;
            }
          }
        }

        this.dataService.sendData(this.dataService.typePassData('load-estimate', {}));

        this.statusChange.emit({
          type,
          convertToInvoice,
          workFlowStatus: this._estimate.workFlowStatus,
          orderStatus: this._estimate.orderStatus,
          estimate: this._estimate,
          openPaymentDialog: isOpenPaymentModal,
        });
        break;
    }
  }

  fetchLatestPDFs(attachments: any[]) {
    if (attachments && attachments.length > 0) {
      let estimatePdf = [];
      let invoicePdf = [];
      let inspectionPdf = [];
      let approvedPdf = [];
      let approvedInvoicePdf = [];
      let approvedEstimateePdf = [];

      attachments.forEach((attachment) => {
        switch (attachment.type) {
          case 'estimate':
            estimatePdf.push({
              ...attachment,
              date: this.dateService.getCurrentDateTimeTz(new Date(attachment.date).getTime()),
            });
            break;

          case 'invoice':
            invoicePdf.push({
              ...attachment,
              date: this.dateService.getCurrentDateTimeTz(new Date(attachment.date).getTime()),
            });
            break;

          case 'inspection':
            inspectionPdf.push({
              ...attachment,
              date: this.dateService.getCurrentDateTimeTz(new Date(attachment.date).getTime()),
            });
            break;

          case 'approved':
            approvedPdf.push({
              ...attachment,
              date: this.dateService.getCurrentDateTimeTz(new Date(attachment.date).getTime()),
            });
            break;

          case 'approved_invoice':
            approvedInvoicePdf.push({
              ...attachment,
              date: this.dateService.getCurrentDateTimeTz(new Date(attachment.date).getTime()),
            });
            break;

          default:
            break;
        }
      });

      estimatePdf = sortBy(estimatePdf, 'date');
      invoicePdf = sortBy(invoicePdf, 'date');
      inspectionPdf = sortBy(inspectionPdf, 'date');
      approvedPdf = sortBy(approvedPdf, 'date');
      approvedInvoicePdf = sortBy(approvedInvoicePdf, 'date');

      if (estimatePdf.length) {
        this.pdfs.estimate = estimatePdf.pop();
      }

      if (invoicePdf.length) {
        this.pdfs.invoice = invoicePdf.pop();
      }

      if (inspectionPdf.length) {
        this.pdfs.inspection = inspectionPdf.pop();
      }

      if (approvedPdf.length) {
        this.pdfs.approved = approvedPdf.pop();
      }

      if (approvedInvoicePdf.length) {
        this.pdfs.approvedInvoice = approvedInvoicePdf.pop();
      }
    }
  }

  async handleDownloadPdf(type: string) {
    await this.getFiles(this._estimate._id);
    window.open(this.pdfs[type].url, '_blank');
  }

  toggleShowMore() {
    this.isOpen = !this.isOpen;
  }

  renderName(name: string) {
    if (name) {
      const [firstName, lastName] = name.split(' ');
      return `${firstName} ${this.helperService.fetchInitials(lastName)}, `;
    }

    return '';
  }

  showToster(options: any) {
    this.showToaster = 'show';
    this.toaster = options;
    this.interval = setTimeout(() => (this.showToaster = 'hide'), 6000);
  }

  onToasterAction(event: any) {
    if (event.action === 'sendInvoice') {
      this.triggerSendInvoice();
    } else if (event.action === 'undo') {
      this.statusChange.emit({
        type: 'orderStatus',
        orderStatus: event.data.orderStatus,
        isUndo: true,
        estimate: { ...this._estimate, orderStatus: event.data.orderStatus },
      });
    }
  }

  triggerSendInvoice() {
    this.sendInvoice.emit(true);
  }

  saveInvoiceDateTime(close: any) {
    if (this.invoiceDateTime.date && this.invoiceDateTime.time) {
      const dateTime = `${this.dateService.formatDate(this.invoiceDateTime.date, datetimeFormat.yyymmdd)} ${this.dateService.formatDate(this.invoiceDateTime.time, datetimeFormat.hhmm)}`;

      if (this.maxDateRange.getTime() > new Date(dateTime).getTime()) {
        this.helperService.showToaster(
          'error',
          `The Invoice generation date must be after RO's creation date  ${this.dateService.formatDate(new Date(this.maxDateRange), datetimeFormat.mmmdyyhhmmaa)}`
        );
        return;
      }

      this.updateInvoiceDateTime.emit(
        `${this.dateService.formatDate(this.invoiceDateTime.date, datetimeFormat.yyymmdd)} ${this.dateService.formatDate(this.invoiceDateTime.time, datetimeFormat.hhmm)}`
      );

      if (close) {
        close();
      }
    }
  }

  openServiceAuthorisationDialog() {
    this.dataService.isFinalizedRo = this.isFinalizedRo;
    this.checkPendingServices.emit(this.authServiceStatusCount.pending !== this._estimate.services.length); // check authorization status
    this.appOpenAuthDialog.emit();
  }

  handleInvCodeChange(value: string) {
    this.newInvCode = value;
    this._handleCheckForUniqueInvCode();
  }

  handleCheckForUniqueInvCode() {
    if (!this.newInvCode) return;

    this.httpService
      .checkForUniqueInvCode({
        invCode: this.newInvCode,
        orderId: this._estimate._id,
      })
      .then(
        ({ isUnique, message }: any) => {
          if (isUnique) {
            this.errors.newInvCode = '';
          } else {
            this.errors.newInvCode = message;
          }
        },
        (err) => {
          this.helperService.showToaster('error', err);
        }
      );
  }

  updateInvCode(close: any) {
    this.isInvoiceCodeUpdating = true;
    this.httpService
      .updateInvoiceCodeManually({
        invCode: this.newInvCode,
        orderId: this._estimate._id,
      })
      .then(
        (res) => {
          this._estimate.invCode = this.newInvCode;
          this.updateInvoiceCode.emit(this.newInvCode);
          this.newInvCode = '';
          this.helperService.showToaster('success', 'Successfully updated');
          if (close) {
            close();
          }
        },
        (err) => {
          this.helperService.showToaster('error', err);
        }
      )
      .finally(() => {
        this.isInvoiceCodeUpdating = false;
      });
  }

  handleVinPrompt({ isMandatoryPrompt, isMileageRequired, isVinRequired, successCallback, failCallback }: any) {
    this.isOrderStatusUpdating = false;

    const ref = this.dialogService.open(MilageInformationDialogComponent, {
      width: '30%',
      styleClass: 'small-heading-dialog',
      showHeader: false,
      data: {
        styleClassHeading: 'font-14',
        orderId: this._estimate._id,
        isMileageRequired,
        isVinRequired,
        isMandatoryPrompt,
      },
    });

    const onClose = ref.onClose.subscribe(({ isSuccess, data }) => {
      if (isSuccess) {
        if (successCallback) {
          successCallback();
        }
      } else {
        if (failCallback) {
          failCallback();
        }
      }

      this.updateOrderStatusProcessFlag.emit(false);

      onClose.unsubscribe();
    });
  }

  openUpdateInvoiceDateDialog() {
    if (this._estimate.isFinalized) return;

    this.maxDateRange = this.dateService.getCurrentDateTimeTz(new Date(this._estimate.backdatedCreationDate ? this._estimate.backdatedCreationDate : this._estimate.createdAt).getTime());
    this.authDate = this.dateService.getCurrentDateTimeTz(new Date(this._estimate.approvedAt).getTime());

    const ref = this.dialogService.open(UpdateInvoiceDateDialogComponent, {
      width: '570px',
      styleClass: 'add-dialog add-payment-modal fixedWidth',
      showHeader: false,
      data: {
        styleClassHeading: 'font-14',
        orderId: this._estimate._id,
        invoiceDateTime: this.invoiceDateTime,
        createdDateTime: {
          date: new Date(this.maxDateRange),
          time: new Date(this.maxDateRange),
        },
        authDateTime: {
          date: new Date(this.authDate),
          time: new Date(this.authDate),
        },
        invoiceBy: this.invoiceBy,
        authorizedBy: this.authorizedBy,
        createdBy: this.createdBy,
      },
    });

    const onClose = ref.onClose.subscribe(({ isSuccess, data }) => {
      if (isSuccess) {
        this.updateInvoiceDateTime.emit(data);
      }
      onClose.unsubscribe();
    });
  }

  handleViewAllFollowUp(viewType: string) {
    if (this.isArchived) return;
    const { vehicle } = this._estimate;

    if (vehicle && vehicle.vehicleId) {
      this.dataService.sendData(
        this.dataService.typeFollowUp('RO', {
          viewType,
          event: 'order_follow_up',
          vehicle: this._estimate.vehicle,
          orderId: this._estimate && this._estimate._id,
          customerId: this._estimate.customer && this._estimate.customer.customerId,
        })
      );
    } else {
      this.helperService.showToaster('error', viewType === 'view-upcoming' ? 'Select vehicle to view follow-ups' : 'Select vehicle to schedule follow-ups');
    }
  }

  clearSourceCampaign(event, campaign) {
    campaign.remove = true;
    if (campaign.campaignId) campaign.parentType = 'followUp';
    if (campaign.marketingCampaignId) campaign.parentType = 'marketingCampaign';
    this.updateSourceCampaign.emit(campaign);
  }

  onSelectPayment(status: any) {
    const payment = status;
    if (isEqual(this._estimate.paymentStatus, payment)) {
      return;
    }
    this._estimate.paymentStatus = status;
    const payload = {
      status: status.id,
    }
    this.httpService.updatePaymentStatus(payload, this._estimate._id).then(x => {
      this._estimate.isDateUpdated = false;
      this.dataService.sendData({
        type: 'app-toaster',
        status: 'success payment-status-toaster',
        message: {
          heading: '',
          body: `
          <div class="flex-start-center">
          <i class="fal fa-check custom-check-icon mr-3"></i>
          <div class="mr-4">Payment status has successfully been updated to ${status.name}
          </div>
          </div>`,
        },
      });

      this.dataService.sendData(this.dataService.typePassData('refresh-estimate', {}));
    }).catch(err => this.helperService.showToaster('error', err))
  }
}
